import { ArrowLeft, Ban, Link2, Loader2, Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import {
  ExternalTaskFilter,
  ExternalTaskRuleType,
  InternalTaskFilter,
  useExternalTaskRuleCreateMutation,
  useExternalTaskRuleLiveTestMutation,
  useExternalTaskRulePartialUpdateMutation,
  useExternalTaskRulePreviewExternalFilterMutation,
  useExternalTaskRulePreviewInternalFilterMutation,
  useExternalTaskRuleRetrieveQuery,
  useExternalTaskRuleUpdateConfigMutation,
} from '@/services/api/firmsService';

import { ExternalTaskFilters } from './components/ExternalTaskFilters';
import { InternalTaskFilters } from './components/InternalTaskFilters';
import { LivePreviewPanel } from './components/LivePreviewPanel';

interface TaskLinkingRuleEditorProps {
  firmId: string;
}

const emptyExternalFilter: ExternalTaskFilter = {
  name_contains: [],
  name_not_contains: [],
  description_contains: [],
  statuses: [],
  assignee_names: [],
};

const emptyInternalFilter: InternalTaskFilter = {
  task_group_ids: [],
  task_type_ids: [],
  firm_task_template_ids: [],
};

const hasAnyFilter = (
  filter: ExternalTaskFilter | InternalTaskFilter,
): boolean => {
  return Object.values(filter).some(
    (arr) => Array.isArray(arr) && arr.length > 0,
  );
};

export const TaskLinkingRuleEditor: React.FC<TaskLinkingRuleEditorProps> = ({
  firmId,
}) => {
  const navigate = useNavigate();
  const { ruleId } = useParams<{ ruleId: string }>();
  const isEditing = Boolean(ruleId);

  // Form state
  const [name, setName] = useState('');
  const [ruleType, setRuleType] = useState<ExternalTaskRuleType>('LINKING');
  const [matchSlaHours, setMatchSlaHours] = useState<string>('');
  const [externalFilter, setExternalFilter] =
    useState<ExternalTaskFilter>(emptyExternalFilter);
  const [internalFilter, setInternalFilter] =
    useState<InternalTaskFilter>(emptyInternalFilter);

  // Queries
  const { data: existingRule, isLoading: isLoadingRule } =
    useExternalTaskRuleRetrieveQuery(
      { firmId, id: ruleId! },
      { skip: !ruleId },
    );

  // Mutations
  const [createRule, { isLoading: isCreating }] =
    useExternalTaskRuleCreateMutation();
  const [updateRule, { isLoading: isUpdatingMeta }] =
    useExternalTaskRulePartialUpdateMutation();
  const [updateConfig, { isLoading: isUpdatingConfig }] =
    useExternalTaskRuleUpdateConfigMutation();

  // Preview mutations
  const [
    liveTest,
    { data: combinedPreviewData, isLoading: isLoadingCombined },
  ] = useExternalTaskRuleLiveTestMutation();
  const [
    previewExternal,
    { data: externalPreviewData, isLoading: isLoadingExternalPreview },
  ] = useExternalTaskRulePreviewExternalFilterMutation();
  const [
    previewInternal,
    { data: internalPreviewData, isLoading: isLoadingInternalPreview },
  ] = useExternalTaskRulePreviewInternalFilterMutation();

  // View mode for preview panel
  const [previewMode, setPreviewMode] = useState<
    'preview' | 'external' | 'internal'
  >('preview');

  // Preview limit state
  const [previewLimit, setPreviewLimit] = useState(500);

  const isSaving = isCreating || isUpdatingMeta || isUpdatingConfig;

  // Populate form when editing existing rule
  useEffect(() => {
    if (existingRule) {
      setName(existingRule.name);
      setRuleType(existingRule.rule_type);
      // Parse ISO 8601 duration to hours
      if (existingRule.match_sla) {
        const hours = parseIsoDurationToHours(existingRule.match_sla);
        setMatchSlaHours(hours > 0 ? String(hours) : '');
      } else {
        setMatchSlaHours('');
      }
      if (existingRule.active_version?.config) {
        setExternalFilter(
          existingRule.active_version.config.external_task_filter ||
            emptyExternalFilter,
        );
        setInternalFilter(
          existingRule.active_version.config.internal_task_filter ||
            emptyInternalFilter,
        );
      }
    }
  }, [existingRule]);

  // Debounced filters for live preview
  const debouncedExternalFilter = useDebouncedValue(externalFilter, 500);
  const debouncedInternalFilter = useDebouncedValue(internalFilter, 500);

  // Trigger external filter preview (always fetch, even without filters)
  useEffect(() => {
    previewExternal({
      firmId,
      previewExternalFilterRequestSchema: {
        firm_id: firmId,
        external_task_filter: debouncedExternalFilter,
        limit: previewLimit,
      },
    });
  }, [debouncedExternalFilter, firmId, previewExternal, previewLimit]);

  // Trigger internal filter preview (always fetch, even without filters)
  useEffect(() => {
    previewInternal({
      firmId,
      previewInternalFilterRequestSchema: {
        firm_id: firmId,
        internal_task_filter: debouncedInternalFilter,
        limit: previewLimit,
      },
    });
  }, [debouncedInternalFilter, firmId, previewInternal, previewLimit]);

  // Trigger combined live preview
  useEffect(() => {
    const hasExternalFilters = hasAnyFilter(debouncedExternalFilter);
    const hasInternalFilters = hasAnyFilter(debouncedInternalFilter);

    // For EXCLUSION rules, only external filters are needed
    // For LINKING rules, both filters are needed
    const shouldRunPreview =
      ruleType === 'EXCLUSION'
        ? hasExternalFilters
        : hasExternalFilters && hasInternalFilters;

    if (shouldRunPreview) {
      liveTest({
        firmId,
        liveTestRequestSchema: {
          firm_id: firmId,
          external_task_filter: debouncedExternalFilter,
          internal_task_filter: debouncedInternalFilter,
          rule_type: ruleType,
          limit: previewLimit,
        },
      });
    }
  }, [
    debouncedExternalFilter,
    debouncedInternalFilter,
    firmId,
    liveTest,
    ruleType,
    previewLimit,
  ]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a rule name');
      return;
    }

    const hasExternalFilters = hasAnyFilter(externalFilter);
    const hasInternalFilters = hasAnyFilter(internalFilter);

    // Validate filters based on rule type
    if (ruleType === 'EXCLUSION') {
      if (!hasExternalFilters) {
        toast.error(
          'Please configure external task filters for exclusion rule',
        );
        return;
      }
    } else if (!hasExternalFilters || !hasInternalFilters) {
      // LINKING rules need both filters
      toast.error('Please configure both external and internal task filters');
      return;
    }

    // Convert hours to ISO 8601 duration (e.g., "PT24H")
    const matchSla = matchSlaHours
      ? hoursToIsoDuration(parseInt(matchSlaHours, 10))
      : null;

    try {
      if (isEditing && ruleId) {
        const metaChanged =
          name !== existingRule?.name ||
          ruleType !== existingRule?.rule_type ||
          matchSla !== existingRule?.match_sla;

        if (metaChanged) {
          await updateRule({
            firmId,
            id: ruleId,
            patchedUpdateExternalTaskRuleSchema: {
              name,
              rule_type: ruleType,
              match_sla: matchSla,
            },
          }).unwrap();
        }

        await updateConfig({
          firmId,
          id: ruleId,
          updateExternalTaskRuleConfigSchema: {
            internal_task_filter: internalFilter,
            external_task_filter: externalFilter,
          },
        }).unwrap();

        toast.success('Rule updated successfully');
      } else {
        await createRule({
          firmId,
          createExternalTaskRuleSchema: {
            firm_id: firmId,
            name,
            description: null,
            rule_type: ruleType,
            match_sla: matchSla,
            internal_task_filter: internalFilter,
            external_task_filter: externalFilter,
          },
        }).unwrap();

        toast.success('Rule created successfully');
      }

      navigate(`/firms/${firmId}/task-linking`);
    } catch (error) {
      toast.error(
        isEditing ? 'Failed to update rule' : 'Failed to create rule',
      );
      console.error(error);
    }
  };

  const handleBack = () => {
    navigate(`/firms/${firmId}/task-linking`);
  };

  if (isLoadingRule) {
    return <LoadingSkeleton />;
  }

  const hasExternalFilters = hasAnyFilter(externalFilter);
  const hasInternalFilters = hasAnyFilter(internalFilter);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="h-9 w-9 rounded-lg hover:bg-slate-100"
          >
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">
              {isEditing ? 'Edit Rule' : 'Create Task Linking Rule'}
            </h1>
            <p className="text-sm text-slate-500">
              {isEditing
                ? 'Modify rule configuration and filters'
                : 'Configure filters to match and link tasks'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleBack}
            className="border-slate-200"
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isEditing ? 'Save Changes' : 'Create Rule'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
        <div className="mx-auto max-w-6xl space-y-6">
          {/* Basic Rule Settings */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-semibold text-slate-900">
                Rule Settings
              </h2>
              <p className="text-xs text-slate-500">
                Configure the basic rule properties
              </p>
            </div>
            <div className="space-y-5 p-5">
              {/* Name Field */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-slate-900">
                  Rule Name <span className="text-red-500">*</span>
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Police Report Linking"
                  maxLength={100}
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-100"
                />
              </div>

              {/* Rule Type and Match SLA */}
              <div className="grid grid-cols-2 gap-5">
                {/* Rule Type */}
                <div className="space-y-2">
                  <div className="text-sm font-medium text-slate-900">
                    Rule Type
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setRuleType('LINKING')}
                      className={cn(
                        'flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-medium transition-all',
                        ruleType === 'LINKING'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                      )}
                    >
                      <Link2 className="h-4 w-4" />
                      Linking
                    </button>
                    <button
                      type="button"
                      onClick={() => setRuleType('EXCLUSION')}
                      className={cn(
                        'flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-medium transition-all',
                        ruleType === 'EXCLUSION'
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                      )}
                    >
                      <Ban className="h-4 w-4" />
                      Exclusion
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    {ruleType === 'LINKING'
                      ? 'Create links between matching tasks'
                      : 'Exclude matching tasks from linking'}
                  </p>
                </div>

                {/* Match SLA (only for LINKING rules) */}
                {ruleType === 'LINKING' && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-slate-900">
                      Match SLA (hours)
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={matchSlaHours}
                      onChange={(e) => setMatchSlaHours(e.target.value)}
                      placeholder="e.g., 24"
                      className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-100"
                    />
                    <p className="text-xs text-slate-500">
                      Expected time to create link after match (optional)
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter Cards - Side by Side */}
          <div className="grid grid-cols-2 gap-6">
            {/* External Task Filter */}
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-5 py-4">
                <h2 className="text-sm font-semibold text-slate-900">
                  External Task Filter
                </h2>
                <p className="text-xs text-slate-500">
                  {ruleType === 'EXCLUSION'
                    ? 'Match external tasks to exclude from linking'
                    : 'Match external tasks from Filevine'}
                </p>
              </div>
              <div className="p-5">
                <ExternalTaskFilters
                  value={externalFilter}
                  onChange={setExternalFilter}
                  firmId={firmId}
                />
              </div>
            </div>

            {/* Internal Task Filter - Disabled for EXCLUSION rules */}
            <div
              className={cn(
                'overflow-hidden rounded-xl border bg-white',
                ruleType === 'EXCLUSION'
                  ? 'border-slate-100 opacity-50'
                  : 'border-slate-200',
              )}
            >
              <div className="border-b border-slate-100 px-5 py-4">
                <h2
                  className={cn(
                    'text-sm font-semibold',
                    ruleType === 'EXCLUSION'
                      ? 'text-slate-400'
                      : 'text-slate-900',
                  )}
                >
                  Internal Task Filter
                </h2>
                <p className="text-xs text-slate-500">
                  {ruleType === 'EXCLUSION'
                    ? 'Not applicable for exclusion rules'
                    : 'Match internal tasks in your system'}
                </p>
              </div>
              <div className="p-5">
                <InternalTaskFilters
                  value={internalFilter}
                  onChange={setInternalFilter}
                  disabled={ruleType === 'EXCLUSION'}
                  firmId={firmId}
                />
              </div>
            </div>
          </div>

          {/* Link Preview with Arrow Connectors */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-semibold text-slate-900">
                Live Preview
              </h2>
              <p className="text-xs text-slate-500">
                See how your filters match tasks in real-time
              </p>
            </div>
            <div className="p-5">
              <LivePreviewPanel
                mode={previewMode}
                onModeChange={setPreviewMode}
                combinedData={combinedPreviewData}
                externalData={externalPreviewData}
                internalData={internalPreviewData}
                isLoadingCombined={isLoadingCombined}
                isLoadingExternal={isLoadingExternalPreview}
                isLoadingInternal={isLoadingInternalPreview}
                hasExternalFilters={hasExternalFilters}
                hasInternalFilters={hasInternalFilters}
                ruleType={ruleType}
                limit={previewLimit}
                onLimitChange={setPreviewLimit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="flex h-full flex-col">
    <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
      <div className="flex items-center gap-4">
        <div className="h-9 w-9 animate-pulse rounded-lg bg-slate-100" />
        <div className="space-y-2">
          <div className="h-5 w-48 animate-pulse rounded-lg bg-slate-100" />
          <div className="h-4 w-64 animate-pulse rounded-lg bg-slate-100" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-10 w-20 animate-pulse rounded-lg bg-slate-100" />
        <div className="h-10 w-32 animate-pulse rounded-lg bg-slate-100" />
      </div>
    </div>
    <div className="flex-1 bg-slate-50 p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="h-48 animate-pulse rounded-xl bg-slate-100" />
        <div className="grid grid-cols-2 gap-6">
          <div className="h-80 animate-pulse rounded-xl bg-slate-100" />
          <div className="h-80 animate-pulse rounded-xl bg-slate-100" />
        </div>
        <div className="h-64 animate-pulse rounded-xl bg-slate-100" />
      </div>
    </div>
  </div>
);

/**
 * Parse ISO 8601 duration (e.g., "PT24H") to hours
 */
function parseIsoDurationToHours(iso: string): number {
  const match = iso.match(/P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return 0;

  const [, days, hours, minutes] = match;
  const d = parseInt(days || '0', 10);
  const h = parseInt(hours || '0', 10);
  const m = parseInt(minutes || '0', 10);

  return d * 24 + h + m / 60;
}

/**
 * Convert hours to ISO 8601 duration (e.g., 24 -> "PT24H")
 */
function hoursToIsoDuration(hours: number): string {
  if (hours <= 0) return 'PT0H';
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (days > 0 && remainingHours > 0) {
    return `P${days}DT${remainingHours}H`;
  }
  if (days > 0) {
    return `P${days}D`;
  }
  return `PT${remainingHours}H`;
}
